import React, { useEffect, useState } from 'react';
import type { EngineStatus, ProviderHealthSnapshot } from '@pagepilot/domain';
import { Card, Button, Field, Badge, ErrorNote } from '../ui';

interface ProviderRow {
  id: string;
  kind: string;
  displayName: string;
  enabled: boolean;
  executablePath: string | null;
  responsibilities: string[];
  extraConfig: Record<string, unknown>;
}

export function ProvidersScreen(props: { status: EngineStatus | null }) {
  const [rows, setRows] = useState<ProviderRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<Record<string, ProviderHealthSnapshot>>({});
  const [xaiApiKeyDraft, setXaiApiKeyDraft] = useState('');

  const load = async () => {
    try {
      setRows((await window.pagepilot.providers.list()) as ProviderRow[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => { void load(); }, []);

  const healthFor = (kind: string): ProviderHealthSnapshot | undefined =>
    checkResult[kind] ?? props.status?.providers.find((p) => p.kind === kind);

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">AI Providers</h1>
      {error && <ErrorNote message={error} />}
      <div className="grid grid-cols-2 gap-4">
        {rows.map((r) => {
          const h = healthFor(r.kind);
          return (
            <Card key={r.kind}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold">{r.displayName}</p>
                  <p className="text-xs text-neutral-500">{r.responsibilities.join(', ')}</p>
                </div>
                <Badge status={h?.status ?? 'OFFLINE'} />
              </div>
              <dl className="mt-3 space-y-1 text-xs text-neutral-400">
                <div className="flex gap-2">
                  <dt className="w-28 text-neutral-600">Executable</dt>
                  <dd className="truncate">{h?.executablePath ?? r.executablePath ?? 'auto-detect on PATH'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-28 text-neutral-600">Version</dt>
                  <dd>{h?.version ?? '—'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-28 text-neutral-600">Last check</dt>
                  <dd>{h ? new Date(h.checkedAt).toLocaleString() : 'not checked yet'}</dd>
                </div>
                {h?.cooldownUntil && (
                  <div className="flex gap-2">
                    <dt className="w-28 text-neutral-600">Cooldown ends</dt>
                    <dd className="text-amber-400">{new Date(h.cooldownUntil).toLocaleString()}</dd>
                  </div>
                )}
                {h?.lastError && <p className="text-red-400">{h.lastError}</p>}
              </dl>

              <div className="mt-3">
                <PathEditor
                  current={r.executablePath}
                  onSave={async (path) => {
                    await window.pagepilot.providers.update(r.kind, { executablePath: path || null });
                    void load();
                  }}
                />
              </div>

              {r.kind === 'grok-video' && (
                <div className="mt-3 rounded-lg border border-neutral-800 p-3">
                  <p className="mb-2 text-xs text-neutral-400">
                    xAI API key for real Grok Imagine video generation. Stored DPAPI-encrypted; only a masked hint is kept. The official flow submits an asynchronous job, polls it, then downloads the completed clip locally.
                  </p>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Field label="xAI API key" type="password" value={xaiApiKeyDraft} onChange={setXaiApiKeyDraft} />
                    </div>
                    <Button
                      disabled={xaiApiKeyDraft.length < 10}
                      onClick={async () => {
                        await window.pagepilot.secrets.set({ name: 'xai_api_key', kind: 'api_key', value: xaiApiKeyDraft });
                        setXaiApiKeyDraft('');
                      }}
                    >
                      Save key
                    </Button>
                  </div>
                </div>
              )}

              <div className="mt-3 flex gap-2">
                <Button
                  variant="ghost"
                  disabled={checking === r.kind}
                  onClick={async () => {
                    setChecking(r.kind);
                    try {
                      const snap = (await window.pagepilot.engine.providerCheck(r.kind)) as ProviderHealthSnapshot;
                      setCheckResult((prev) => ({ ...prev, [r.kind]: snap }));
                    } catch (e) {
                      setError(e instanceof Error ? e.message : String(e));
                    } finally {
                      setChecking(null);
                    }
                  }}
                >
                  {checking === r.kind ? 'Testing…' : 'Test connection'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={async () => {
                    await window.pagepilot.providers.update(r.kind, { enabled: !r.enabled });
                    void load();
                  }}
                >
                  {r.enabled ? 'Disable' : 'Enable'}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function PathEditor(props: { current: string | null; onSave: (path: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(props.current ?? '');
  if (!editing) {
    return (
      <button className="text-xs text-brand-300 hover:underline" onClick={() => { setValue(props.current ?? ''); setEditing(true); }}>
        Configure executable path
      </button>
    );
  }
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Field label="Executable path (empty = auto-detect)" value={value} onChange={setValue} placeholder="C:\\tools\\bin\\tool.exe" />
      </div>
      <Button onClick={async () => { await props.onSave(value.trim()); setEditing(false); }}>Save</Button>
      <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
    </div>
  );
}
